import { z } from 'zod';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { DescriptionMaxLength, withFallback } from './types.dto';

export const CustomFieldType = z.enum([
  'NUMBER',
  'LIST',
  'TEXT',
  'USER',
  'FORMULA',
  'CURRENCY',
  'PERCENT',
  'RATING',
  'DATE',
  'DATE_RANGE',
  'FIBONACCI',
]);

// Deduplicated Fibonacci sequence (first 20 unique values)
export const FIBONACCI_SEQUENCE = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144] as const;
export type FibonacciValue = (typeof FIBONACCI_SEQUENCE)[number];

export const CustomFieldTypeDto = {
  NUMBER: 'NUMBER',
  TEXT: 'TEXT',
  LIST: 'LIST',
  USER: 'USER',
  FORMULA: 'FORMULA',
  CURRENCY: 'CURRENCY',
  PERCENT: 'PERCENT',
  RATING: 'RATING',
  DATE: 'DATE',
  DATE_RANGE: 'DATE_RANGE',
  FIBONACCI: 'FIBONACCI',
} as const;

export type CustomFieldTypeDtoType = (typeof CustomFieldTypeDto)[keyof typeof CustomFieldTypeDto];

export const SystemCustomFieldName = {
  Impact: '_Impact',
  Confidence: '_Confidence',
  Effort: '_Effort',
  Reach: '_Reach',
  Value: '_Value',
} as const;

export type SystemCustomFieldNameType =
  (typeof SystemCustomFieldName)[keyof typeof SystemCustomFieldName];

export const FormulaSystemCustomFieldName = {
  RICEScore: '_R.I.C.E. Score',
  ValueVsEffort: '_Value vs Effort',
} as const;

export type FormulaSystemCustomFieldNameType =
  (typeof FormulaSystemCustomFieldName)[keyof typeof FormulaSystemCustomFieldName];

/*

These field configuration zod schemas for the JSONB data will be used to:
- Provide compile-time type checking for DB JSONB data being inserted or selected via drizzle
- Provide zod validation for API request and response data
- Provide type checking for the custom field logic on the front and back end

Notes for consideration when making changes:

1) If you change the shape of the zod schema, you will need to migrated existing data in the DB.

    See previous migrations in libs/fim/drizzle/src/db/migrations/*.sql for examples.
    Look for jsonb_agg() and jsonb_set() functions as a starting point.

2) The zod number properties which are not required need to be .nullish() instead of .optional()

    This is because the front end has been sometimes sending/storing null values for number properties, and sometimes
    omitting the properties entirely. Until this quirk is fixed, we need to allow for null values in the zod schema.
    If you want to convert to .optional(), you will need to migrate the DB data and strip out any null properties. That is possible, but complex.
    If you just change to .optional() without a DB migration, you will get runtime API errors as non-compliant data is fetched from the DB.

3) isPositiveFactor indicates if the field is a positive factor (e.g., reach, impact) vs. negative factor (e.g., complexity, effort)

*/

export const DateFieldConfigurationSchema = z.object({
  fieldType: z.enum(['DATE', 'date']),
});
export type DateFieldConfigurationDto = z.infer<typeof DateFieldConfigurationSchema>;

export const DateRangeFieldConfigurationSchema = z.object({
  fieldType: z.enum(['DATE_RANGE', 'date_range']),
});
export type DateRangeFieldConfigurationDto = z.infer<typeof DateRangeFieldConfigurationSchema>;

export const UserFieldConfigurationSchema = z.object({
  fieldType: z.enum(['USER', 'user']),
  notifyUsers: z.boolean().optional(),
});
export type UserFieldConfigurationDto = z.infer<typeof UserFieldConfigurationSchema>;

export const RatingFieldConfigurationSchema = z.object({
  fieldType: z.enum(['RATING', 'rating']),
  isPositiveFactor: z.boolean().default(true), // See note 3) above
});
export type RatingFieldConfigurationDto = z.infer<typeof RatingFieldConfigurationSchema>;

export const FibonacciFieldConfigurationSchema = z.object({
  fieldType: z.enum(['FIBONACCI', 'fibonacci']),
  maxValue: z.number().refine((val) => FIBONACCI_SEQUENCE.includes(val as FibonacciValue), {
    message: 'maxValue must be a valid Fibonacci number from the sequence',
  }),
  isPositiveFactor: z.boolean().default(true), // See note 3) above
});
export type FibonacciFieldConfigurationDto = z.infer<typeof FibonacciFieldConfigurationSchema>;

export const NumberFieldConfigurationSchema = z
  .object({
    fieldType: z.enum(['NUMBER', 'number']),
    places: z
      .number()
      .transform((n) => Math.max(Math.min(n, 10), -10))
      .nullish(), // See note 2) above
    increment: z.number().nullish(), // See note 2) above
    minimum: z.number().nullish(), // See note 2) above
    maximum: z.number().nullish(), // See note 2) above
    isPositiveFactor: z.boolean().default(true), // See note 3) above
  })
  .refine((config) => !config.minimum || !config.maximum || config.minimum <= config.maximum);
export type NumberFieldConfigurationDto = z.infer<typeof NumberFieldConfigurationSchema>;

export const PercentFieldConfigurationSchema = z
  .object({
    fieldType: z.enum(['PERCENT', 'percent']),
    places: z
      .number()
      .transform((n) => Math.max(Math.min(n, 10), -10))
      .nullish(), // See note 2) above
    increment: z.number().nullish(), // See note 2) above
    minimum: z.number().nullish(), // See note 2) above
    maximum: z.number().nullish(), // See note 2) above
    isPositiveFactor: z.boolean().default(true), // See note 3) above
  })
  .refine((config) => !config.minimum || !config.maximum || config.minimum <= config.maximum);
export type PercentFieldConfigurationDto = z.infer<typeof PercentFieldConfigurationSchema>;

export const CurrencyFieldConfigurationSchema = z
  .object({
    fieldType: z.enum(['CURRENCY', 'currency']),
    places: z
      .number()
      .transform((n) => Math.max(Math.min(n, 10), -10))
      .nullish(), // See note 2) above
    increment: z.number().nullish(), // See note 2) above
    minimum: z.number().nullish(), // See note 2) above
    maximum: z.number().nullish(), // See note 2) above
    currency: z.string().optional(),
    isPositiveFactor: z.boolean().default(true), // See note 3) above
  })
  .refine((config) => !config.minimum || !config.maximum || config.minimum <= config.maximum);
export type CurrencyFieldConfigurationDto = z.infer<typeof CurrencyFieldConfigurationSchema>;

export const ListFieldConfigurationSchema = z.object({
  fieldType: z.enum(['LIST', 'list']),
  select: z.union([z.literal('single'), z.literal('multi')]),
  options: z.array(z.string()),
  enableNumericValues: z.boolean().optional(),
  numericValues: z.array(z.number().optional()).optional(),
  isPositiveFactor: z.boolean().optional().default(false), // to be used with numericValues, hence optional
});
export type ListFieldConfigurationDto = z.infer<typeof ListFieldConfigurationSchema>;

export const InputFieldsSchema = z.array(
  z.object({
    fieldId: z.number(),
    fieldName: z.string(),
    fieldExtents: z
      .array(z.object({ start: z.number().min(0), end: z.number().min(0) }))
      .optional(),
  }),
);
export type InputFieldsDto = z.infer<typeof InputFieldsSchema>;

export const FormulaFieldConfigurationSchema = z.object({
  fieldType: z.enum(['FORMULA', 'formula']),
  formula: z.string(),
  inputFields: InputFieldsSchema,
  places: z
    .number()
    .transform((n) => Math.max(Math.min(n, 10), -10))
    .nullish(), // See note 2) above
  isPositiveFactor: z.boolean().default(true), // See note 3) above
});
export type FormulaFieldConfigurationDto = z.infer<typeof FormulaFieldConfigurationSchema>;

export const TextFieldConfigurationSchema = z.object({
  fieldType: z.enum(['TEXT', 'text']),
  validationRegex: z.string().optional(),
  maxLength: z.number().nullish(), // See note 2) above
  multiline: z.boolean().optional(),
  rows: withFallback(z.number().min(1).nullish(), undefined), // See note 2) above
});
export type TextFieldConfigurationDto = z.infer<typeof TextFieldConfigurationSchema>;

export const FieldConfigurationSchema = z
  .discriminatedUnion('fieldType', [
    DateFieldConfigurationSchema,
    DateRangeFieldConfigurationSchema,
    UserFieldConfigurationSchema,
    RatingFieldConfigurationSchema,
    FibonacciFieldConfigurationSchema,
    NumberFieldConfigurationSchema,
    PercentFieldConfigurationSchema,
    CurrencyFieldConfigurationSchema,
    ListFieldConfigurationSchema,
    FormulaFieldConfigurationSchema,
    TextFieldConfigurationSchema,
  ])
  .refine((config) => {
    if (
      'minimum' in config &&
      'maximum' in config &&
      typeof config.minimum === 'number' &&
      typeof config.maximum === 'number' &&
      config.minimum > config.maximum
    )
      return false;
    else return true;
  });
export type FieldConfigurationDto = z.infer<typeof FieldConfigurationSchema>;

const validCustomFieldNameRegex = /^[a-zA-Z0-9 _.-]+$/;

export const CustomFieldSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(300).regex(validCustomFieldNameRegex, {
    message: 'Name contains invalid characters.',
  }),
  description: z.string().max(DescriptionMaxLength).nullish(),
  fieldConfiguration: FieldConfigurationSchema,
  importedFieldId: z.string().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  isSystem: z.boolean().optional(),
  priorityFrameworkId: z.number().nullish(),
});

export type CustomFieldDto = z.infer<typeof CustomFieldSchema>;

export const GetCustomFieldsResponseSchema = inferPaginatedApiResponse(z.array(CustomFieldSchema));
export type GetCustomFieldsResponseDto = z.infer<typeof GetCustomFieldsResponseSchema>;

export const GetCustomFieldsUnPaginatedResponseSchema = inferApiResultSchema(
  z.array(CustomFieldSchema),
);
export type GetCustomFieldsUnPaginatedResponseSchemaDto = z.infer<
  typeof GetCustomFieldsUnPaginatedResponseSchema
>;

export const GetCustomFieldResponseSchema = inferApiResultSchema(CustomFieldSchema);
export type GetCustomFieldResponseDto = z.infer<typeof GetCustomFieldResponseSchema>;
export type GetCustomFieldsUnPaginatedResponseDto = z.infer<
  typeof GetCustomFieldsUnPaginatedResponseSchema
>;

export const GetCustomFieldsByIdsResponseSchema = inferApiResultSchema(z.array(CustomFieldSchema));
export type GetCustomFieldsByIdsResponseDto = z.infer<typeof GetCustomFieldsByIdsResponseSchema>;

export const GetCustomFieldUsageResponseSchema = z.object({
  id: z.number(),
  // The next five counts are from drizzle's db.$count() function, which returns a bigint as a string. Yuck
  feedbackCount: z.string().transform((val) => Number.parseInt(val, 10)),
  ideaCount: z.string().transform((val) => Number.parseInt(val, 10)),
  customerCount: z.string().transform((val) => Number.parseInt(val, 10)),
  formCount: z.string().transform((val) => Number.parseInt(val, 10)),
  ideaViewCount: z.string().transform((val) => Number.parseInt(val, 10)),
});
export type GetCustomFieldUsageResponseDto = z.infer<typeof GetCustomFieldUsageResponseSchema>;

export const CreateCustomFieldSchema = CustomFieldSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  isSystem: true,
});
export type CreateCustomFieldDto = z.infer<typeof CreateCustomFieldSchema>;

export const CreateSystemCustomFieldSchema = CustomFieldSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
});
export type CreateSystemCustomFieldDto = z.infer<typeof CreateSystemCustomFieldSchema>;

export const CreateCustomFieldResponseSchema = inferApiResultSchema(CustomFieldSchema);
export type CreateCustomFieldResponseDto = z.infer<typeof CreateCustomFieldResponseSchema>;

export const CustomFieldsSchema = z.array(CustomFieldSchema);
export const CreateCustomFieldsResponseSchema = inferApiResultSchema(CustomFieldsSchema);
export type CreateCustomFieldsResponseDto = z.infer<typeof CreateCustomFieldsResponseSchema>;

export const UpdateCustomFieldSchema = CreateCustomFieldSchema.partial();
export type UpdateCustomFieldDto = z.infer<typeof UpdateCustomFieldSchema>;

export const UpdateCustomFieldResponseSchema = inferApiResultSchema(CustomFieldSchema);
export type UpdateCustomFieldResponseDto = z.infer<typeof UpdateCustomFieldResponseSchema>;
